import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import { MonthCalendar } from "../components/MonthCalendar";
import { SkeletonList } from "../components/Skeleton";
import type { MeetListItem } from "../api/types";

function UpcomingCard({ m }: { m: MeetListItem }) {
  return (
    <div className="card">
      <div className="row spread">
        <Link to={`/meets/${m.meetId}`}>
          <strong>{m.title}</strong>
        </Link>
        <span className="row" style={{ gap: "0.35rem" }}>
          {m.status === "cancelled" && (
            <span className="badge cancelled">cancelled</span>
          )}
          {m.myRsvp && <span className="badge state">You: {m.myRsvp}</span>}
        </span>
      </div>
      <p className="muted">
        {new Date(m.startAt).toLocaleString(undefined, {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })}
        {m.venueName ? ` · ${m.venueName}` : ""}
        {typeof m.distanceMiles === "number" ? ` · ${m.distanceMiles} mi` : ""}
      </p>
      <div className="row">
        <span className="badge going">Going {m.rsvpCounts.going}</span>
        <span className="badge">Maybe {m.rsvpCounts.maybe}</span>
      </div>
    </div>
  );
}

/**
 * My Meets — the post-login home. Month-at-a-glance calendar of the meets on
 * the user's teams, an upcoming list, and clear paths over to Discover.
 */
export function MyMeets() {
  // Calendar is great on desktop; list is far more usable on a phone — default
  // to list on narrow screens (user can still toggle).
  const [view, setView] = useState<"calendar" | "list">(
    typeof window !== "undefined" && window.innerWidth <= 720
      ? "list"
      : "calendar",
  );
  const [showCancelled, setShowCancelled] = useState(false);
  const { data, loading, error } = useAsync(
    () => api.listMeets({ scope: "mine", sort: "date" }),
    [],
  );
  const me = useAsync(() => api.getMe(), []);
  const [hype, setHype] = useState<string>();
  const firstTeamId = me.data?.teams?.[0]?.teamId;
  useEffect(() => {
    if (!firstTeamId) return;
    let cancelled = false;
    api
      .getTeamHype(firstTeamId)
      .then((r) => !cancelled && r.text && setHype(r.text))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [firstTeamId]);

  const items = data?.items ?? [];
  // Cancelled meets are hidden by default (they shouldn't clutter the planner),
  // but an opt-in toggle reveals them so you can still see what was cancelled.
  const cancelledCount = items.filter((m) => m.status === "cancelled").length;
  const active = showCancelled
    ? items
    : items.filter((m) => m.status !== "cancelled");
  const now = Date.now();
  const upcoming = active
    .filter((m) => new Date(m.endAt).getTime() >= now)
    .sort((a, b) => a.startAt.localeCompare(b.startAt));

  return (
    <div>
      <div className="home-hero">
        <div className="home-hero-body">
          <h1 className="home-hero-title">My Meets</h1>
          <p className="home-hero-hype">
            {hype ?? "Your season, your team — let's go."}
          </p>
          {upcoming[0] && (
            <p className="home-hero-next">
              Next up:{" "}
              <strong>{upcoming[0].title}</strong> ·{" "}
              {new Date(upcoming[0].startAt).toLocaleDateString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
              })}
            </p>
          )}
        </div>
        <div className="home-hero-actions row" style={{ gap: "0.4rem" }}>
          <Link to="/meets/new">
            <button className="secondary">+ New meet</button>
          </Link>
          {cancelledCount > 0 && (
            <button
              className={showCancelled ? "" : "secondary"}
              aria-pressed={showCancelled}
              onClick={() => setShowCancelled((v) => !v)}
              title="Show or hide cancelled meets"
            >
              {showCancelled ? "Hide" : "Show"} cancelled ({cancelledCount})
            </button>
          )}
          <div className="seg">
            <button
              className={view === "calendar" ? "seg-on" : ""}
              onClick={() => setView("calendar")}
            >
              Calendar
            </button>
            <button
              className={view === "list" ? "seg-on" : ""}
              onClick={() => setView("list")}
            >
              List
            </button>
          </div>
        </div>
      </div>

      <p className="muted">
        <Link to="/discover" className="link-strong">
          Looking for more meets? →
        </Link>
      </p>

      {loading && <SkeletonList count={4} variant="card" />}
      {error && <p className="error">{error}</p>}

      {data && active.length === 0 && (
        <div className="card cal-empty">
          <h2>No meets yet</h2>
          <p className="muted">
            {items.length > 0
              ? "Your only meets are cancelled. Find tournaments near you and add them to a team so everyone can RSVP, carpool, and get reminders."
              : "You're not tracking any meets. Find tournaments near you and add them to a team so everyone can RSVP, carpool, and get reminders."}
          </p>
          <div className="row" style={{ gap: "0.6rem", marginTop: "0.5rem" }}>
            <Link to="/onboarding">
              <button>Join your teams</button>
            </Link>
            <Link to="/discover">
              <button className="secondary">Discover meets</button>
            </Link>
            <Link to="/meets/new">
              <button className="secondary">Create a meet manually</button>
            </Link>
          </div>
        </div>
      )}

      {data && active.length > 0 && (
        <>
          {view === "calendar" ? (
            <MonthCalendar meets={active} />
          ) : (
            <>
              {upcoming.length === 0 && (
                <p className="muted">No upcoming meets. Check the calendar for past ones.</p>
              )}
              <div className="stagger">
                {upcoming.map((m) => (
                  <UpcomingCard key={m.meetId} m={m} />
                ))}
              </div>
            </>
          )}

          {view === "calendar" && upcoming.length > 0 && (
            <>
              <h2 style={{ marginTop: "1.25rem" }}>Next up</h2>
              {upcoming.slice(0, 5).map((m) => (
                <UpcomingCard key={m.meetId} m={m} />
              ))}
            </>
          )}

          <div className="card" style={{ textAlign: "center" }}>
            <p className="muted" style={{ margin: 0 }}>
              Want to add more tournaments?{" "}
              <Link to="/discover" className="link-strong">
                Browse Discover →
              </Link>
            </p>
          </div>
        </>
      )}
    </div>
  );
}
